import { FUEL_ORDER_STATUS_LABELS, type FuelOrder, fuelOrderStatusSchema } from "@station/contracts";
import { Money } from "@station/core";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClipboardList, Lightbulb, Plus } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { FUEL_ORDER_STATUS_TONE, orderTotalLitres } from "../../../../features/fuel/order-values.js";
import { useFuelOrders, useFuelPermissions } from "../../../../features/fuel/queries.js";
import {
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  useFuelStation,
} from "../../../../features/fuel/shared.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate } from "../../../../lib/format.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...fuelSearchShape,
  status: fuelOrderStatusSchema.optional().catch(undefined),
  from: date,
  to: date,
});

export const Route = createFileRoute("/_app/akaryakit/siparisler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: FuelOrderListPage,
});

function FuelOrderListPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelOrders")}
      description={t("fuel.orders.list.description")}
      breadcrumb={[{ label: t("menu.fuelOrders") }]}
      actions={
        <>
          <Button variant="outline" asChild>
            <Link to="/akaryakit/siparis-onerileri">
              <Lightbulb aria-hidden="true" />
              {t("menu.fuelOrderSuggestions")}
            </Link>
          </Button>
          {permissions.canManageOrders ? (
            <Button asChild>
              <Link to="/akaryakit/siparisler/yeni">
                <Plus aria-hidden="true" />
                {t("fuel.orders.new.title")}
              </Link>
            </Button>
          ) : null}
        </>
      }
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <FuelOrderList companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function FuelOrderList({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useFuelStation(companyId);
  const setFilters = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value))) as never,
      replace: true,
    });

  const filters = {
    stationId: station.stationId ?? undefined,
    status: search.status,
    from: search.from,
    to: search.to,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useFuelOrders(companyId, filters, pages.cursor);
  const stationNames = useMemo(
    () => new Map(station.stations.map((item) => [item.id, `${item.code} ${item.name}`])),
    [station.stations],
  );

  const columns = useMemo<DataTableColumn<FuelOrder>[]>(
    () => [
      {
        id: "orderNo",
        header: t("fuel.orders.fields.orderNo"),
        hideable: false,
        exportValue: (row) => row.orderNo,
        cell: (row) => (
          <Link
            to="/akaryakit/siparisler/$id"
            params={{ id: row.id }}
            className="font-mono underline underline-offset-4"
          >
            {row.orderNo}
          </Link>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => FUEL_ORDER_STATUS_LABELS[row.status],
        cell: (row) => (
          <StatusBadge tone={FUEL_ORDER_STATUS_TONE[row.status]}>
            {FUEL_ORDER_STATUS_LABELS[row.status]}
          </StatusBadge>
        ),
      },
      {
        id: "station",
        header: t("fuel.station"),
        exportValue: (row) => stationNames.get(row.stationId) ?? null,
        cell: (row) => stationNames.get(row.stationId) ?? "—",
      },
      {
        id: "distributor",
        header: t("fuel.orders.fields.distributor"),
        exportValue: (row) => row.distributorName,
        cell: (row) => row.distributorName,
      },
      {
        id: "requestedDeliveryDate",
        header: t("fuel.orders.fields.requestedDeliveryDate"),
        exportValue: (row) => row.requestedDeliveryDate,
        cell: (row) => formatDate(row.requestedDeliveryDate),
      },
      {
        id: "quantityL",
        header: t("fuel.orders.fields.totalL"),
        align: "right",
        exportValue: (row) => orderTotalLitres(row.lines).toString(),
        cell: (row) => `${orderTotalLitres(row.lines).formatTr(3)} L`,
      },
      {
        id: "estimatedAmount",
        header: t("fuel.orders.fields.estimatedAmount"),
        align: "right",
        exportValue: (row) => row.estimatedAmount,
        cell: (row) => (row.estimatedAmount === null ? "—" : Money.fromMinor(row.estimatedAmount).formatTr()),
      },
    ],
    [t, stationNames],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <DataTable
      caption={t("fuel.orders.list.caption")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<ClipboardList />}
          title={t("fuel.orders.list.emptyTitle")}
          description={t("fuel.orders.list.emptyDescription")}
        />
      }
      filters={
        <>
          <StationField selection={station} />
          <FormField label={t("common.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) =>
                  setFilters({
                    status:
                      event.target.value === "" ? undefined : fuelOrderStatusSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {fuelOrderStatusSchema.options.map((status) => (
                  <option key={status} value={status}>
                    {FUEL_ORDER_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("fuel.orders.list.from")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={search.from ?? null}
                onValueChange={(value) => setFilters({ from: value ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("fuel.orders.list.to")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={search.to ?? null}
                onValueChange={(value) => setFilters({ to: value ?? undefined })}
              />
            )}
          </FormField>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
